import React from "react";
import { Button, Placeholder, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faCopy } from "@fortawesome/free-solid-svg-icons";
import LostTerminalActionsMenu from "./LostTerminalActionsMenu";
import { formatDate, normalizeText, resolveActionLabel } from "./lostTerminalAlertsUtils";

const priorityClassByValue = {
  CRITICA: "is-critical",
  ALTA: "is-high",
  MEDIA: "is-medium",
  BAJA: "is-low",
};

const PRIORITY_LABELS = {
  CRITICA: "Crítica",
  ALTA: "Alta",
  MEDIA: "Media",
  BAJA: "Baja",
};

export default function LostTerminalAlertCard({
  item,
  expanded = false,
  detail = null,
  detailLoading = false,
  detailError = "",
  copied = false,
  onToggle,
  onManage,
  onTake,
  onAssign,
  onRelease,
  onCopyMessage,
}) {
  const priorityKey = normalizeText(item.priority).toUpperCase();
  const priorityClassName = priorityClassByValue[priorityKey] || "is-neutral";
  const detailData = detail || {};
  const actionLabel = resolveActionLabel(item);
  const metaItems = [
    { key: "cuit", label: "CUIT", value: item.cuit },
    { key: "terminal", label: "Terminal", value: item.terminal },
    { key: "province", label: "Provincia", value: item.province },
    { key: "phone", label: "Teléfono", value: item.phone },
  ].filter((meta) => meta.value);

  const badges = [detailData.irregularity, detailData.segment].filter(Boolean);

  return (
    <article className={`lost-terminal-alert-card ${expanded ? "is-expanded" : ""}`}>
      <div className="lost-terminal-alert-card__head">
        <button
          type="button"
          className="lost-terminal-alert-card__summary"
          onClick={onToggle}
          aria-expanded={expanded}
        >
          <div className="lost-terminal-alert-card__main">
            <strong>{item.commerceName}</strong>
            {metaItems.length ? (
              <div className="lost-terminal-alert-card__meta">
                {metaItems.map((meta) => (
                  <span key={meta.key} className="lost-terminal-alert-card__meta-item">
                    <b>{meta.label}:</b> {meta.value}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        </button>

        <div className="lost-terminal-alert-card__controls" onClick={(event) => event.stopPropagation()}>
          <div className="lost-terminal-alert-card__badges">
            <span className="lost-terminal-alert-card__pill">{item.status}</span>
            <span className={`lost-terminal-alert-card__advance ${item.advanceActive.toLowerCase() === "activo" ? "is-active" : "is-inactive"}`}>
              {item.advanceActive.toLowerCase() === "activo" ? "Activo" : "Inactivo"}
            </span>
            {item.score !== null ? <span className="lost-terminal-alert-card__pill">Score {item.score}</span> : null}
            {item.priority ? (
              <span className={`lost-terminal-alert-card__priority ${priorityClassName}`}>
                {PRIORITY_LABELS[priorityKey] || item.priority}
              </span>
            ) : null}
          </div>

          <LostTerminalActionsMenu
            item={item}
            onManage={onManage}
            onTake={onTake}
            onAssign={onAssign}
            onRelease={onRelease}
            onCopyMessage={onCopyMessage}
          />

          <button
            type="button"
            className="lost-terminal-alert-card__chevron"
            onClick={onToggle}
            aria-label={expanded ? "Contraer terminal extraviada" : "Expandir terminal extraviada"}
            aria-expanded={expanded}
          >
            <FontAwesomeIcon icon={faChevronDown} className={expanded ? "is-open" : ""} />
          </button>
        </div>
      </div>

      {expanded ? (
        <div className="lost-terminal-alert-card__detail">
          {detailLoading ? (
            <div className="lost-terminal-alert-card__detail-loading">
              <Placeholder as="div" animation="glow"><Placeholder xs={12} /></Placeholder>
              <Placeholder as="div" animation="glow"><Placeholder xs={10} /></Placeholder>
              <Placeholder as="div" animation="glow"><Placeholder xs={8} /></Placeholder>
            </div>
          ) : detailError ? (
            <div className="lost-terminal-alert-card__detail-error">
              <span>{detailError}</span>
            </div>
          ) : (
            <>
              <div className="lost-terminal-alert-card__detail-column">
                <div className="lost-terminal-alert-card__detail-title-row">
                  <h4>Terminal extraviada</h4>
                  {badges.length ? (
                    <div className="lost-terminal-alert-card__detail-mini-badges">
                      {badges.map((badge) => (
                        <span key={badge} className="lost-terminal-alert-card__pill">{badge}</span>
                      ))}
                    </div>
                  ) : null}
                </div>

                <div className="lost-terminal-alert-card__detail-lines">
                  {detailData.reason ? <p><b>Motivo:</b> {detailData.reason}</p> : null}
                  {detailData.suggestedAction ? <p><b>Acción sugerida:</b> {detailData.suggestedAction}</p> : null}
                  {detailData.context ? <p><b>Contexto:</b> {detailData.context}</p> : null}
                  {detailData.lastContactAt ? <p><b>Último contacto:</b> {formatDate(detailData.lastContactAt) || detailData.lastContactAt}</p> : null}
                  {detailData.nextContactAt ? <p><b>Próximo contacto:</b> {formatDate(detailData.nextContactAt) || detailData.nextContactAt}</p> : null}
                  {(detailData.responsibleName || item.responsibleName) ? <p><b>Responsable:</b> {detailData.responsibleName || item.responsibleName}</p> : null}
                </div>
              </div>

              <div className="lost-terminal-alert-card__detail-column is-message">
                {detailData.suggestedMessage || item.suggestedMessage ? (
                  <p className="lost-terminal-alert-card__message-text">
                    <b className="lost-terminal-alert-card__message-label">Mensaje sugerido:</b>{" "}
                    <span>“{detailData.suggestedMessage || item.suggestedMessage}”</span>
                  </p>
                ) : (
                  <p className="lost-terminal-alert-card__message-empty">
                    <b className="lost-terminal-alert-card__message-label">Mensaje sugerido:</b> Sin mensaje sugerido disponible.
                  </p>
                )}
                <Button
                  type="button"
                  variant="link"
                  className="lost-terminal-alert-card__copy"
                  onClick={() => onCopyMessage(detailData.suggestedMessage || item.suggestedMessage)}
                >
                  <FontAwesomeIcon icon={faCopy} />
                  <span>{copied ? "Mensaje copiado" : "Copiar"}</span>
                </Button>

                <div className="lost-terminal-alert-card__detail-action">
                  <Button
                    type="button"
                    className="lost-terminal-alert-card__manage-btn"
                    onClick={() => onManage?.(item.raw)}
                    disabled={detailLoading}
                  >
                    {detailLoading ? <Spinner animation="border" size="sm" /> : actionLabel}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      ) : null}
    </article>
  );
}
